import { Grid } from "@mui/material";
import { observer } from "mobx-react";
import React, { useCallback, useMemo, useState } from "react";
import { Suggestion } from "../Suggestions/Suggestion";
import { useStore } from "utils/useStore";
import { useNavigate } from "react-router";
import { ApplicationFiltersStore } from "stores/ApplicationFilters/ApplicationFiltersStore";
import { kboFiltersToApplicationFilters } from "../../AgentUtils";
import RootStore from "stores/RootStore";
import SignalsPageStore from "components/Signals/Store/SignalsPageStore";
import DashboardStore from "components/Signals/Dashboard/DashboardStore";
import {
    ADDITIONAL_CHARTS_DASHBOARD,
    ISignalsChartDefinition,
    SignalsObjectType,
} from "components/Signals/SignalsModels";
import { SelectedCardRequestPayload } from "services/SignalsService";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import { MAX_DASHBOARD_CHARTS } from "components/Signals/Dashboard/chartLimit";
import { AddToDashboardDialog } from "./AddToDashboardDialog";

const canAddTo = (dashboard: DashboardStore) =>
    !dashboard.pinnedApplicationFilterId &&
    dashboard.signalsChartCount < MAX_DASHBOARD_CHARTS;

export const AddPreExistingGraphToDashboard = observer(({ args, remove }) => {
    const navigate = useNavigate();

    const signalsPageStore = useStore(SignalsPageStore);
    const applicationFiltersStore = useStore(ApplicationFiltersStore);
    const messageStore = useStore(MessageStore);
    const [dialogOpen, setDialogOpen] = useState(false);

    const chartDefinition = useMemo(() => {
        const dashboardsToSearch = [
            ...signalsPageStore.standardDashboards,
            ...signalsPageStore.allDashboardsArray.filter(
                (dash) => dash.title === ADDITIONAL_CHARTS_DASHBOARD,
            ),
        ];

        for (const dashboard of dashboardsToSearch) {
            const chart = dashboard.signalsObjects.find(
                (obj) =>
                    obj.objectType === SignalsObjectType.Chart &&
                    obj.dataEndpoint === args.chartId,
            );
            if (chart) return chart as ISignalsChartDefinition;
        }

        return undefined;
    }, [
        args.chartId,
        signalsPageStore.allDashboardsArray,
        signalsPageStore.standardDashboards,
    ]);
    const addToDashboard = useCallback(
        async (dashboard: DashboardStore) => {
            const dashboardId = dashboard.id;

            if (!chartDefinition) {
                messageStore.logError(
                    "Unable to find the requested chart definition.",
                );
                return false;
            }

            const payload: SelectedCardRequestPayload = {
                type: SignalsObjectType.Chart,
                info: {
                    dataEndpoint: chartDefinition.dataEndpoint,
                    title: chartDefinition.title,
                    subtitle: chartDefinition.subtitle,
                    xTitle: chartDefinition.xTitle,
                    yTitle: chartDefinition.yTitle,
                    vizOptions: chartDefinition.vizOptions,
                    typeOfChart: chartDefinition.typeOfChart,
                    typeOfDataSelectedInChart:
                        chartDefinition.typeOfDataSelectedInChart,
                    availableRenderModes: chartDefinition.availableRenderModes,
                    defaultRenderMode: chartDefinition.defaultRenderMode,
                    isPaginated: chartDefinition.isPaginated,
                    compareToPreviousModeIsAvailable:
                        chartDefinition.compareToPreviousModeIsAvailable,
                    isTemporal: chartDefinition.isTemporal,
                    showDataGrid: chartDefinition.showDataGrid,
                    showSpotlight: chartDefinition.showSpotlight,
                    showAnalyzeWithKBo: chartDefinition.showAnalyzeWithKBo,
                    objectFilters: chartDefinition.objectFilters,
                    orderRuleId: chartDefinition.orderRuleId,
                },
            };

            try {
                if (!(await dashboard.addCards([payload]))) return false;

                applicationFiltersStore.resetAllFilters();

                if (args.filters) {
                    const filters = await kboFiltersToApplicationFilters(
                        args.filters,
                    );
                    applicationFiltersStore.applyExternalFilters(filters, true);
                }

                const dashboardUrlToNavigateTo = `/app/signals/dashboard/${dashboardId}`;
                const isAlreadyViewingDashboard =
                    dashboardUrlToNavigateTo ===
                    RootStore().activeLocation.location;

                if (isAlreadyViewingDashboard) {
                    signalsPageStore.setCurrentDashboard({ dashboardId });
                    await dashboard.applyFilters?.();
                } else {
                    navigate(dashboardUrlToNavigateTo);
                }

                remove();

                return true;
            } catch (error) {
                messageStore.logError("Failed to add chart to dashboard.");
                return false;
            }
        },
        [
            args,
            applicationFiltersStore,
            chartDefinition,
            messageStore,
            navigate,
            remove,
            signalsPageStore,
        ],
    );

    if (!args.chartId || !args.filters) return null;

    return (
        <React.Fragment>
            <AddToDashboardDialog
                id="kbo-add-graph-to-dashboard"
                isOpen={dialogOpen}
                onClose={() => setDialogOpen(false)}
                canAddTo={canAddTo}
                isAddDisabled={!chartDefinition}
                onAdd={addToDashboard}
            />
            <Grid item>
                <Suggestion onClick={() => setDialogOpen((open) => !open)}>
                    {args?.buttonText || "Visualize this data over time"}
                </Suggestion>
            </Grid>
        </React.Fragment>
    );
});
